import { Navigate, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuthContext } from '@/contexts/AuthContext'
import { AuthState } from '@/types/auth'
import { buildLoginRedirectFor, getSafePostLoginPath, toFullPath, type PathLike } from '@/utils/authRedirect'

/**
 * 全屏 Loading 组件
 */
function FullScreenLoading({ message, subMessage }: { message: string; subMessage?: string }) {
  return (
    <div className="fixed inset-0 flex items-center justify-center bg-background z-50">
      <div className="text-center">
        {/* Spinner */}
        <div className="relative w-16 h-16 mx-auto mb-4">
          <div className="absolute top-0 left-0 w-full h-full">
            <div className="w-16 h-16 border-4 border-primary/30 border-t-primary rounded-full animate-spin"></div>
          </div>
        </div>

        {/* Message */}
        <p className="text-lg font-medium text-foreground mb-1">{message}</p>
        {subMessage && <p className="text-sm text-muted-foreground">{subMessage}</p>}
      </div>
    </div>
  )
}

/**
 * 认证错误页面
 */
function AuthErrorPage({
  error,
  onRetry,
  onBackToLogin,
  showRetry = true,
}: {
  error?: string
  onRetry: () => void
  onBackToLogin: () => void
  showRetry?: boolean
}) {
  const { t } = useTranslation('chrome')
  return (
    <div className="fixed inset-0 flex items-center justify-center bg-muted z-50">
      <div className="max-w-md w-full mx-4">
        <div className="bg-card rounded-xl shadow-lg p-8 text-center">
          {/* Error Icon */}
          <div className="w-16 h-16 mx-auto mb-4 bg-red-100 dark:bg-red-950/50 rounded-full flex items-center justify-center">
            <svg
              className="w-8 h-8 text-red-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </div>

          {/* Title */}
          <h1 className="text-2xl font-bold text-foreground mb-2">{t('auth.errorTitle')}</h1>

          {/* Error Message */}
          <p className="text-muted-foreground mb-6">
            {error || t('auth.errorDefault')}
          </p>

          {/* Actions */}
          <div className={showRetry ? 'flex gap-3' : 'flex'}>
            {showRetry && (
              <button
                onClick={onRetry}
                className="flex-1 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 font-medium transition-colors"
              >
                {t('auth.retry')}
              </button>
            )}
            <button
              onClick={onBackToLogin}
              className="flex-1 px-4 py-2 bg-muted text-muted-foreground rounded-lg hover:bg-gray-300 font-medium transition-colors"
            >
              {t('auth.backToLogin')}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

/**
 * 认证守卫组件
 *
 * 用途：
 * 1. 保护需要认证的路由
 * 2. 根据认证状态自动重定向
 * 3. 显示适当的 loading 或错误状态
 *
 * 使用方式：
 * ```tsx
 * // 公开路由
 * <AuthGuard requireAuth={false}>
 *   <LoginPage />
 * </AuthGuard>
 *
 * // Onboarding 路由
 * <AuthGuard requireAuth={true} allowOnboarding={true}>
 *   <OnboardingPage />
 * </AuthGuard>
 *
 * // 受保护路由
 * <AuthGuard requireAuth={true}>
 *   <Dashboard />
 * </AuthGuard>
 * ```
 */
export function AuthGuard({
  children,
  requireAuth = true,
  allowOnboarding = false,
}: {
  children: React.ReactNode
  /** 是否需要完全认证 */
  requireAuth?: boolean
  /** 是否允许 onboarding 状态访问（用于 onboarding 页面） */
  allowOnboarding?: boolean
}) {
  const { t } = useTranslation('chrome')
  const { state, error, isRecoverableError, refreshAuth, signOut } = useAuthContext()
  const location = useLocation()

  // 处理 Back to Login：清理 Clerk session 后跳转
  const handleBackToLogin = async () => {
    try {
      // 先清理 Clerk session
      await signOut()
      // 清理本地存储（防止残留）
      localStorage.clear()
      // 跳转到登录页
      window.location.href = '/login'
    } catch (err) {
      console.error('Failed to sign out:', err)
      // 即使退出失败，也强制跳转并清理本地存储
      localStorage.clear()
      window.location.href = '/login'
    }
  }

  // ===== 1. 初始化状态 - 显示 Loading =====
  if (state === AuthState.INITIALIZING) {
    return <FullScreenLoading message={t('auth.initializingMessage')} subMessage={t('auth.pleaseWait')} />
  }

  // ===== 2. 同步用户状态 - 显示 Loading =====
  if (state === AuthState.NEED_USER_SYNC) {
    return <FullScreenLoading message={t('auth.syncingMessage')} subMessage={t('auth.pleaseWait')} />
  }

  // ===== 3. 错误状态 - 显示错误页面 =====
  if (state === AuthState.AUTH_ERROR) {
    return (
      <AuthErrorPage
        error={error}
        onRetry={refreshAuth}
        onBackToLogin={handleBackToLogin}
        showRetry={isRecoverableError !== false}
      />
    )
  }

  // ===== 4. 未认证 =====
  if (state === AuthState.UNAUTHENTICATED) {
    // 如果当前页面需要认证，重定向到登录。
    // 回跳目标写进 `?from=`（完整 pathname+search+hash）而不是只放 router state：
    // Clerk 登录流程会整页跳转（OAuth 回跳等），内存里的 state 到不了对面，
    // 回跳会退化成 /dashboard，深链（如 /intents?intentId=123）就丢了。
    // state 仍然保留，兼容既有读取路径。
    if (requireAuth) {
      return <Navigate to={buildLoginRedirectFor(location)} state={{ from: location }} replace />
    }

    // 如果是公开页面（如登录/注册页），直接显示
    return <>{children}</>
  }

  // ===== 5. 需要 Onboarding =====
  if (state === AuthState.NEEDS_ONBOARDING) {
    // 如果当前页面允许 onboarding 状态访问（比如 onboarding 页面本身）
    if (allowOnboarding) {
      return <>{children}</>
    }

    // 如果当前页面不允许 onboarding 状态，重定向到 onboarding
    return <Navigate to="/onboarding/organization" replace />
  }

  // ===== 6. 已完全认证 =====
  if (state === AuthState.AUTHENTICATED) {
    // 如果用户已认证，但还在 onboarding 页面，重定向到 Dashboard
    if (location.pathname.startsWith('/onboarding')) {
      return <Navigate to="/dashboard" replace />
    }

    // 如果用户已认证，在登录/注册页面，重定向到原本想访问的页面或 Dashboard
    if (location.pathname === '/login' || location.pathname === '/register') {
      // 回跳目标优先级：
      // 1. URL ?from=（401 整页刷新 / AuthGuard 拦截场景写入，能活过整页跳转）
      // 2. location.state.from（同进程内的路由拦截兜底）
      // 3. /dashboard（默认）
      // ⚠️ state 兜底要取 pathname+search+hash，只取 pathname 会把深链的
      //    ?intentId= / #hash 丢掉。
      const fromQuery = new URLSearchParams(location.search).get('from')
      const fromState = toFullPath((location.state as { from?: PathLike } | null)?.from)
      const target = getSafePostLoginPath(fromQuery) || getSafePostLoginPath(fromState) || '/dashboard'
      return <Navigate to={target} replace />
    }

    // 正常访问
    return <>{children}</>
  }

  // ===== 7. 未知状态（理论上不应该到这里） =====
  return <FullScreenLoading message={t('auth.loadingMessage')} subMessage={t('auth.pleaseWait')} />
}
